import { Link, useNavigate, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import API from "../api/api";
import { BACKEND_URL } from "../api/api";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import CameraFileInput from "../components/CameraFileInput";
import PublicFooter from "../components/PublicFooter";
import { contactErrors, vatError } from "../utils/contactValidation";
import toast from "react-hot-toast";

const parseList = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatPrice = (value) => value || value === 0 ? `EUR ${value}` : "-";

export default function TrailerDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [selectedDateMode, setSelectedDateMode] = useState("start");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [form, setForm] = useState({
    firstName: "",
    name: "",
    phone: "",
    email: "",
    streetName: "",
    houseNumber: "",
    busNumber: "",
    postalCode: "",
    city: "",
    wantsInvoice: false,
    vatNumber: ""
  });
  const [files, setFiles] = useState({
    idCardFront: null,
    idCardBack: null,
    licenseFront: null,
    licenseBack: null,
    vehicleReport: null
  });
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const validationErrors = contactErrors(form.phone, form.email);
  const vatValidationError = vatError(form.vatNumber);

  const { data: trailers = [], isLoading: trailersLoading } = useQuery({
    queryKey: ["trailers"],
    queryFn: async () => (await API.get("/trailers")).data
  });

  const { data: reservations = [], isLoading: reservationsLoading } = useQuery({
    queryKey: ["reservations"],
    queryFn: async () => (await API.get("/reservations")).data
  });

  const trailer = useMemo(
    () => trailers.find((item) => item.id === Number(id)),
    [trailers, id]
  );

  const photos = parseList(trailer?.photos);
  const options = parseList(trailer?.options);

  const address = [
    form.streetName,
    form.houseNumber,
    form.busNumber ? `bus ${form.busNumber}` : "",
    form.postalCode,
    form.city
  ].filter(Boolean).join(" ");

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    const res = await API.post("/upload", formData);
    return res.data.filePath;
  };

  const isEndBeforeStart = (startDate, endDate) => (
    startDate && endDate && new Date(endDate) < new Date(startDate)
  );

  const toggleOption = (option) => {
    setSelectedOptions((current) => (
      current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option]
    ));
  };

  const reservationMutation = useMutation({
    mutationFn: async () => {
      if (!trailer) throw new Error("Selecteer eerst een trailer");
      if (!selectedStartDate || !selectedEndDate) throw new Error("Selecteer een start- en einddatum");
      if (isEndBeforeStart(selectedStartDate, selectedEndDate)) {
        throw new Error("Einddatum mag niet vóór startdatum liggen.");
      }
      if (!form.firstName || !form.name || !form.phone || !form.email) {
        throw new Error("Vul alle klantgegevens in");
      }
      if (validationErrors.phone || validationErrors.email) {
        throw new Error(validationErrors.phone || validationErrors.email);
      }
      if (!form.streetName || !form.houseNumber || !form.postalCode || !form.city) {
        throw new Error("Vul straatnaam, huisnummer, postcode en gemeente in");
      }
      if (form.wantsInvoice && !form.vatNumber) {
        throw new Error("BTW-nummer is verplicht voor een factuur");
      }
      if (form.vatNumber && vatValidationError) {
        throw new Error(vatValidationError);
      }
      if (!files.idCardFront || !files.idCardBack || !files.licenseFront || !files.licenseBack || !files.vehicleReport) {
        throw new Error("Upload ID-kaart voorkant, ID-kaart achterkant, rijbewijs voorkant, rijbewijs achterkant en identificatieverslag van de wagen");
      }

      const idCardDocs = [
        await uploadFile(files.idCardFront),
        await uploadFile(files.idCardBack)
      ];
      const licenseDocs = [
        await uploadFile(files.licenseFront),
        await uploadFile(files.licenseBack)
      ];
      const vehicleDocs = [
        await uploadFile(files.vehicleReport)
      ];
      const documents = JSON.stringify({
        idCardFront: idCardDocs[0],
        idCardBack: idCardDocs[1],
        licenseFront: licenseDocs[0],
        licenseBack: licenseDocs[1],
        vehicleReport: vehicleDocs[0],
        idCardDocs,
        licenseDocs,
        vehicleDocs
      });
      const commonPayload = {
        firstName: form.firstName,
        lastName: form.name,
        address,
        streetName: form.streetName,
        houseNumber: form.houseNumber,
        busNumber: form.busNumber,
        postalCode: form.postalCode,
        city: form.city,
        phone: form.phone,
        email: form.email,
        wantsInvoice: form.wantsInvoice,
        vatNumber: form.wantsInvoice ? form.vatNumber : "",
        documents
      };
      const customer = await API.post("/customers", commonPayload);

      const reservationPayload = {
        firstName: form.firstName,
        name: form.name,
        address,
        streetName: form.streetName,
        houseNumber: form.houseNumber,
        busNumber: form.busNumber,
        postalCode: form.postalCode,
        city: form.city,
        phone: form.phone,
        email: form.email,
        wantsInvoice: form.wantsInvoice,
        vatNumber: form.wantsInvoice ? form.vatNumber : "",
        customerId: customer.data.id,
        trailerId: trailer.id,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        selectedOptions: JSON.stringify(selectedOptions),
        idCardDocs: JSON.stringify(idCardDocs),
        licenseDocs: JSON.stringify(licenseDocs),
        vehicleDocs: JSON.stringify(vehicleDocs)
      };

      const reservation = await API.post("/reservations", reservationPayload);
      return { reservation: reservation.data, customer: customer.data, payload: reservationPayload };
    },
    onSuccess: ({ reservation, payload }) => {
      toast.success("Reservatie succesvol aangevraagd");
      navigate("/reservatie-succes", {
        state: {
          reservation,
          trailer,
          customer: {
            ...form,
            address,
            startDate: selectedStartDate,
            endDate: selectedEndDate
          },
          selectedOptions,
          payload
        }
      });
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || "Reservatie aanvragen mislukt")
  });

  const selectDate = (dateValue) => {
    if (selectedDateMode === "start" || !selectedStartDate) {
      setSelectedStartDate(dateValue);
      if (selectedEndDate && !isEndBeforeStart(dateValue, selectedEndDate)) {
        setSelectedDateMode(null);
        return;
      }

      if (selectedEndDate) {
        toast.error("Kies een nieuwe einddatum voor deze startdatum.");
      }

      setSelectedEndDate("");
      setSelectedDateMode("end");
      return;
    }

    if (!selectedDateMode) {
      toast.error("Klik eerst op startdatum of einddatum om die aan te passen.");
      return;
    }

    if (isEndBeforeStart(selectedStartDate, dateValue)) {
      toast.error("Einddatum mag niet vóór startdatum liggen.");
      return;
    }

    setSelectedEndDate(dateValue);
    setSelectedDateMode(null);
  };

  const dateModeText = selectedDateMode === "start"
    ? "Klik in de kalender om de startdatum te kiezen."
    : selectedDateMode === "end"
      ? "Klik in de kalender om de einddatum te kiezen."
      : "Klik op startdatum of einddatum om de periode aan te passen.";

  return (
    <main className="public-shell">
      <aside className="public-sidebar">
        <div className="sidebar-brand">
          <p className="sidebar-brand-title">DR-Renting</p>
          <p className="sidebar-brand-subtitle">Aanbod</p>
        </div>
        <Link className="sidebar-button" to="/">Homepagina</Link>
        <Link className="sidebar-button active" to="/">Trailers</Link>
        <Link className="sidebar-button" to="/contact">Contact</Link>
        {user && <Link className="sidebar-button" to="/dashboard">Dashboard</Link>}
        <div className="public-sidebar-bottom">
          {user ? (
            <button className="sidebar-button danger" onClick={onLogout}>Afmelden</button>
          ) : (
            <Link className="sidebar-button" to="/login">Medewerker login</Link>
          )}
        </div>
      </aside>

      <section className="public-page">
        {trailersLoading || reservationsLoading ? (
          <div className="loading-state">Trailer laden...</div>
        ) : !trailer ? (
          <div className="panel panel-pad public-intro">
            <h1 className="page-title">Trailer niet gevonden</h1>
            <p className="page-subtitle">Deze trailer bestaat niet of is niet meer beschikbaar.</p>
            <Link className="button secondary" to="/">Terug naar homepagina</Link>
          </div>
        ) : (
          <div className="trailer-detail-layout">
            <section className="panel panel-pad">
              <div className="detail-hero">
                <div>
                  <Link className="text-link" to="/">Terug naar overzicht</Link>
                  <h1 className="page-title">{trailer.name}</h1>
                  <p className="page-subtitle">{trailer.extraInfo || "Bekijk alle technische gegevens, foto's en beschikbaarheid."}</p>
                </div>
                <div className="status-line">
                  <span className="badge info">{formatPrice(trailer.pricePerDay)} per dag</span>
                  <span className="badge warning">Waarborg {formatPrice(trailer.depositAmount || 0)}</span>
                </div>
              </div>

              <div className="detail-section">
                <h2 className="detail-title">Foto's</h2>
                {photos.length === 0 ? (
                  <div className="empty-state">Nog geen foto's beschikbaar.</div>
                ) : (
                  <div className="trailer-gallery">
                    {photos.map((photo) => (
                      <button key={photo} className="gallery-thumb-button" onClick={() => setPreviewPhoto(photo)}>
                        <img src={`${BACKEND_URL}${photo}`} alt={trailer.name} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h2 className="detail-title">Technische gegevens</h2>
                <div className="detail-grid">
                  <div className="detail-item"><div className="detail-label">Rijbewijs</div><div className="detail-value">{trailer.licenseType || "-"}</div></div>
                  <div className="detail-item"><div className="detail-label">MTM trailer</div><div className="detail-value">{trailer.mtm || "-"} kg</div></div>
                  <div className="detail-item"><div className="detail-label">Leeggewicht</div><div className="detail-value">{trailer.emptyWeight || "-"} kg</div></div>
                  <div className="detail-item"><div className="detail-label">Max gewicht te laden</div><div className="detail-value">{trailer.maxLoadWeight || "-"} kg</div></div>
                  <div className="detail-item"><div className="detail-label">Lengte</div><div className="detail-value">{trailer.length || "-"} m</div></div>
                  <div className="detail-item"><div className="detail-label">Breedte</div><div className="detail-value">{trailer.width || "-"} m</div></div>
                  <div className="detail-item"><div className="detail-label">Hoogte</div><div className="detail-value">{trailer.height || "-"} m</div></div>
                  <div className="detail-item"><div className="detail-label">Waarborg</div><div className="detail-value">{formatPrice(trailer.depositAmount || 0)}</div></div>
                </div>
              </div>

              <div className="detail-section">
                <h2 className="detail-title">Prijzen</h2>
                <div className="detail-grid">
                  <div className="detail-item"><div className="detail-label">Per dag</div><div className="detail-value">{formatPrice(trailer.pricePerDay)}</div></div>
                  <div className="detail-item"><div className="detail-label">Per week</div><div className="detail-value">{formatPrice(trailer.pricePerWeek)}</div></div>
                  <div className="detail-item"><div className="detail-label">Per maand</div><div className="detail-value">{formatPrice(trailer.pricePerMonth)}</div></div>
                </div>
              </div>

              <div className="detail-section">
                <h2 className="detail-title">Opties</h2>
                {options.length === 0 ? (
                  <p className="page-subtitle">Geen extra opties opgegeven.</p>
                ) : (
                  <div className="option-select-grid">
                    {options.map((option) => {
                      const active = selectedOptions.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          className={`option-choice${active ? " active" : ""}`}
                          onClick={() => toggleOption(option)}
                          aria-pressed={active}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <aside className="panel panel-pad detail-panel">
              <div className="detail-section">
                <h2 className="detail-title">Beschikbaarheid</h2>
                <p className="page-subtitle">Datums in het verleden en bezette datums zijn niet selecteerbaar.</p>
              </div>
              <AvailabilityCalendar
                trailerId={trailer.id}
                reservations={reservations}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                onSelectDate={selectDate}
              />
              <div className="detail-section">
                <h2 className="detail-title">Gekozen periode</h2>
                <p className="page-subtitle">{dateModeText}</p>
                <div className="detail-grid">
                  <button
                    type="button"
                    className={`date-edit-card${selectedDateMode === "start" ? " active" : ""}`}
                    onClick={() => setSelectedDateMode("start")}
                  >
                    <span className="detail-label">Startdatum</span>
                    <span className="detail-value">{selectedStartDate || "-"}</span>
                  </button>
                  <button
                    type="button"
                    className={`date-edit-card${selectedDateMode === "end" ? " active" : ""}`}
                    onClick={() => {
                      if (!selectedStartDate) {
                        toast.error("Kies eerst een startdatum.");
                        setSelectedDateMode("start");
                        return;
                      }
                      setSelectedDateMode("end");
                    }}
                  >
                    <span className="detail-label">Einddatum</span>
                    <span className="detail-value">{selectedEndDate || "-"}</span>
                  </button>
                </div>
              </div>
              <div className="detail-section">
                <h2 className="detail-title">Reservatie aanvragen</h2>
                <div className="form-grid">
                  <label className="field"><span className="field-label">Voornaam</span><input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Naam</span><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Telefoonnummer</span><input className="input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />{validationErrors.phone && <span className="field-error">{validationErrors.phone}</span>}</label>
                  <label className="field"><span className="field-label">E-mail</span><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />{validationErrors.email && <span className="field-error">{validationErrors.email}</span>}</label>
                </div>
                <div className="form-grid" style={{ marginTop: 12 }}>
                  <label className="field"><span className="field-label">Straatnaam</span><input className="input" value={form.streetName} onChange={(e) => setForm({ ...form, streetName: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Huisnummer</span><input className="input" value={form.houseNumber} onChange={(e) => setForm({ ...form, houseNumber: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Busnummer optioneel</span><input className="input" value={form.busNumber} onChange={(e) => setForm({ ...form, busNumber: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Postcode</span><input className="input" value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></label>
                  <label className="field"><span className="field-label">Gemeente</span><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
                </div>
                <label className="check-field">
                  <input type="checkbox" checked={form.wantsInvoice} onChange={(e) => setForm({ ...form, wantsInvoice: e.target.checked, vatNumber: e.target.checked ? form.vatNumber : "" })} />
                  <span>Ik wens een factuur</span>
                </label>
                {form.wantsInvoice && (
                  <label className="field" style={{ marginTop: 12 }}>
                    <span className="field-label">BTW-nummer</span>
                    <input className="input" value={form.vatNumber} onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} />
                    {vatValidationError && <span className="field-error">{vatValidationError}</span>}
                  </label>
                )}
              </div>
              <div className="detail-section">
                <h2 className="detail-title">Documenten uploaden</h2>
                <div className="document-upload-grid">
                  <CameraFileInput id="id-card-front" label="Identiteitskaart voorkant" value={files.idCardFront ? [files.idCardFront] : []} onChange={(file) => setFiles({ ...files, idCardFront: file })} />
                  <CameraFileInput id="id-card-back" label="Identiteitskaart achterkant" value={files.idCardBack ? [files.idCardBack] : []} onChange={(file) => setFiles({ ...files, idCardBack: file })} />
                  <CameraFileInput id="license-front" label="Rijbewijs voorkant" value={files.licenseFront ? [files.licenseFront] : []} onChange={(file) => setFiles({ ...files, licenseFront: file })} />
                  <CameraFileInput id="license-back" label="Rijbewijs achterkant" value={files.licenseBack ? [files.licenseBack] : []} onChange={(file) => setFiles({ ...files, licenseBack: file })} />
                  <CameraFileInput id="vehicle-report" label="Identificatieverslag wagen" value={files.vehicleReport ? [files.vehicleReport] : []} onChange={(file) => setFiles({ ...files, vehicleReport: file })} />
                </div>
                <button className="button full-width-button" onClick={() => reservationMutation.mutate()} disabled={reservationMutation.isPending}>
                  {reservationMutation.isPending ? "Aanvragen..." : "Reservatie aanvragen"}
                </button>
              </div>
            </aside>
          </div>
        )}
        <PublicFooter />
      </section>
      {previewPhoto && (
        <button className="image-modal" onClick={() => setPreviewPhoto(null)}>
          <span className="modal-close" aria-hidden="true">Sluiten</span>
          <img src={`${BACKEND_URL}${previewPhoto}`} alt="Trailer groot" onClick={(event) => event.stopPropagation()} />
        </button>
      )}
    </main>
  );
}
